import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabase';
import { mockGigs, type Gig } from '@/mocks/gigs';
import { mapDbGig, formatGigPrice } from '@/lib/gigs';
import { useAuth } from '@/context/AuthContext';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';

function WorkDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { copied, copy } = useCopyToClipboard();

  const [gig, setGig] = useState<Gig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [alreadyOrdered, setAlreadyOrdered] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState(false);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('gigs')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (err) throw err;
      if (data) {
        setGig(mapDbGig(data as never));
      } else {
        setGig(mockGigs.find((g) => g.id === id) || null);
      }
    } catch {
      setGig(mockGigs.find((g) => g.id === id) || null);
    } finally {
      setLoading(false);
    }
  };

  const checkOrder = async () => {
    if (!user || !id) return;
    const { data } = await supabase
      .from('gig_orders')
      .select('id')
      .eq('gig_id', id)
      .eq('user_id', user.id)
      .maybeSingle();
    setAlreadyOrdered(!!data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    checkOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, id]);

  const handleOrder = async () => {
    if (!user) {
      navigate('/auth/login', { state: { from: { pathname: `/work/${id}` } } });
      return;
    }
    if (!gig) return;
    setOrdering(true);
    setFormError('');
    const { error: err } = await supabase.from('gig_orders').insert({
      gig_id: gig.id,
      user_id: user.id,
    });
    setOrdering(false);
    if (err) {
      setFormError(err.message);
      return;
    }
    setAlreadyOrdered(true);
    setSuccess(true);
  };

  const deliverables = gig?.deliverables ? gig.deliverables.split('\n').filter(Boolean) : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-500/10">
            <i className="ri-loader-4-line text-2xl text-accent-400 animate-spin" />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!gig) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-accent-500/10">
              <i className="ri-error-warning-line text-3xl text-accent-400" />
            </div>
            <h1 className="font-heading font-bold text-2xl text-foreground-50 mb-2">
              Service introuvable
            </h1>
            <p className="text-sm text-foreground-400 mb-6">
              Ce service n&apos;existe plus ou a été retiré.
            </p>
            <Link
              to="/work"
              className="inline-block px-6 py-2.5 bg-accent-500 text-foreground-950 font-semibold rounded-lg hover:bg-accent-400 transition-colors text-sm"
            >
              Voir tous les services
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <main className="flex-1">
        <div className="max-w-5xl mx-auto px-4 md:px-6 pt-24 md:pt-32 pb-16">
          {/* Header */}
          <div className="mb-6">
            <nav aria-label="Breadcrumb" className="text-xs text-foreground-400 mb-4 flex items-center gap-2 flex-wrap">
              <Link to="/work" className="hover:text-accent-400 transition-colors">Work</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-500">{gig.category}</span>
            </nav>

            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5">
              <div>
                <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50 leading-tight">
                  {gig.title}
                </h1>
                <p className="text-foreground-400 mt-1">
                  Par{' '}
                  <Link
                    to={`/freelancers/${gig.ownerId}`}
                    className="font-medium text-foreground-50 hover:text-accent-400 transition-colors"
                  >
                    {gig.freelancer}
                  </Link>
                </p>
                <div className="flex items-center gap-2 flex-wrap mt-3">
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-accent-500/10 text-accent-400 whitespace-nowrap">
                    {gig.category}
                  </span>
                  {gig.rating !== null && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-accent-400 whitespace-nowrap">
                      <i className="ri-star-fill" />
                      {gig.rating.toFixed(1)}
                      <span className="font-normal text-foreground-400">
                        ({gig.reviewsCount} avis)
                      </span>
                    </span>
                  )}
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-map-pin-line" />
                    {gig.location}
                  </span>
                  {gig.deliveryDays !== null && (
                    <span className="text-xs text-foreground-400 flex items-center gap-1">
                      <i className="ri-time-line" />
                      Livraison en {gig.deliveryDays} jours
                    </span>
                  )}
                </div>
              </div>
              <div className="text-left md:text-right shrink-0 flex flex-col gap-3">
                <p className="font-heading font-bold text-2xl text-accent-400 whitespace-nowrap">
                  {formatGigPrice(gig.price)}
                </p>
                <button
                  type="button"
                  onClick={() => copy(window.location.href)}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-background-300/60 text-foreground-50 rounded-lg text-sm font-medium hover:bg-background-200/60 transition-colors whitespace-nowrap"
                  aria-label={`Partager ${gig.title}`}
                  title="Copier le lien"
                >
                  <i className={copied ? 'ri-check-line text-accent-400' : 'ri-share-line'} />
                  {copied ? 'Lien copié !' : 'Partager'}
                </button>
              </div>
            </div>
          </div>

          {/* Success banner */}
          {success && (
            <div className="p-4 rounded-xl bg-accent-500/10 border border-accent-400/30 text-sm text-accent-400 mb-6 flex items-center gap-3">
              <i className="ri-checkbox-circle-line text-xl" />
              Votre commande a bien été enregistrée ! Le freelance vous contactera pour démarrer le projet.
            </div>
          )}

          {error && (
            <div className="p-4 rounded-xl bg-red-500/10 border border-red-400/30 text-sm text-red-400 mb-6">{error}</div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main content */}
            <div className="lg:col-span-2 space-y-6">
              <div className="relative h-56 md:h-72 rounded-xl overflow-hidden">
                <img
                  src={gig.cover}
                  alt={`Service ${gig.title}`}
                  title={`${gig.title} - ${gig.category}`}
                  className="w-full h-full object-cover"
                />
              </div>

              <section className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                <h2 className="font-heading font-semibold text-lg text-foreground-50 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-500/10">
                    <i className="ri-file-text-line text-accent-400" />
                  </span>
                  À propos de ce service
                </h2>
                <p className="text-sm text-foreground-400 leading-relaxed whitespace-pre-line">
                  {gig.description}
                </p>
              </section>

              {deliverables.length > 0 && (
                <section className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <h2 className="font-heading font-semibold text-lg text-foreground-50 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-500/10">
                      <i className="ri-check-double-line text-accent-400" />
                    </span>
                    Ce qui est inclus
                  </h2>
                  <ul className="space-y-2.5">
                    {deliverables.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-foreground-400">
                        <i className="ri-checkbox-circle-line text-accent-400 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 lg:sticky lg:top-24">
                <h3 className="font-heading font-semibold text-foreground-50 mb-4">
                  Commander ce service
                </h3>

                {formError && (
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400 mb-4">
                    {formError}
                  </div>
                )}

                {alreadyOrdered ? (
                  <div className="p-4 rounded-lg bg-accent-500/10 border border-accent-400/30 text-center">
                    <i className="ri-checkbox-circle-line text-2xl text-accent-400" />
                    <p className="text-sm font-semibold text-foreground-50 mt-2">Commande enregistrée</p>
                    <p className="text-xs text-foreground-400 mt-1">
                      Le freelance vous contactera rapidement.
                    </p>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={handleOrder}
                      disabled={ordering}
                      className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-accent-500 text-foreground-950 font-semibold rounded-lg hover:bg-accent-400 transition-colors text-sm whitespace-nowrap mb-3 disabled:opacity-60"
                    >
                      <i className="ri-shopping-cart-line" />
                      {ordering ? 'Commande...' : gig.price !== null && gig.price > 0
                        ? `Commander · ${formatGigPrice(gig.price)}`
                        : 'Demander un devis'}
                    </button>
                    {!user && (
                      <p className="text-xs text-foreground-400 text-center">
                        Connectez-vous pour commander ce service.
                      </p>
                    )}
                  </>
                )}

                <div className="mt-5 pt-5 border-t border-background-200/70 space-y-2 text-sm">
                  {gig.deliveryDays !== null && (
                    <div className="flex items-center gap-2 text-foreground-400">
                      <i className="ri-time-line text-accent-400" />
                      Livraison en {gig.deliveryDays} jours
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-foreground-400">
                    <i className="ri-shield-check-line text-accent-400" />
                    Paiement sécurisé par escrow
                  </div>
                  <div className="flex items-center gap-2 text-foreground-400">
                    <i className="ri-refresh-line text-accent-400" />
                    Révisions incluses
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default WorkDetail;